import {AlarmOutlined} from '@mui/icons-material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import usePrevious from '../../../hooks/usePrevious';
import {
  AlarmHistory,
  AlarmHistoryListQuery,
  AlarmHistoryListResponse,
} from '../../../interfaces/AlarmsHistory';
import reduxActions from '../../../redux/actions';
import {getHumanReadable} from '../../../utils/macAddress';
import {getAlarmNodeHistoryId} from '../../../utils/message-types';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {MapLayerId} from '../../common/Map';
import TabLabel from '../../common/TabLabel';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import MessageTypeSelect from '../../selectors/MessageTypeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: AlarmTrackingReportsData;
  onUpdate?: (value?: AlarmTrackingReportsData) => void;
}

interface AlarmTrackingReportsDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: string[];
  selectAll: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    message_type?: number[];
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface AlarmTrackingReportsData {
  activeId?: number;
  mapLayers?: MapLayerId[];
  mapLevel?: number | null;
  openedItems: AlarmTrackingReportsDataTab[];
}

export const getAlarmTrackingReportsData = (): AlarmTrackingReportsData => ({
  activeId: undefined,
  mapLayers: ['employee_history', 'mine'],
  mapLevel: null,
  openedItems: [],
});

const getAlarmTrackingReportsDataTab = (
  id: number
): AlarmTrackingReportsDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

const DEFAULT_SHOWN_FIELDS = [
  'id',
  'type',
  'reason',
  'reason_note',
  'message_type',
  'user_id',
  'source_mac_address',
  'mac_address',
  'date',
];
const MESSAGE_TYPE = [
  {
    id: 0,
    label: 'All',
  },
  {
    id: 1,
    label: 'Heartbeat',
  },
  {
    id: 50,
    label: 'Control',
  },
] as const;

const MESSAGE_TYPE_OPTIONS = [
  {
    code: 1,
    name: 'Heartbeat',
  },
  {
    code: 50,
    name: 'Control',
  },
];

export const AlarmHistoryTrackingReports = ({value, onUpdate}: Props) => {
  const reduxDispatch = useAppDispatch();

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, value]);

  const activationReason = useAppSelector(
    ({assets}) => assets.activationReason
  );

  const config = useMemo(() => {
    const v = value ?? getAlarmTrackingReportsData();
    return {
      ...v,
      mapLayers: v.mapLayers ?? ['employee_history', 'street', 'mine'],
      mapLevel: v.mapLevel ?? null,
    };
  }, [value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getAlarmTrackingReportsDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const alarms = useAppSelector(({assets}) =>
    assets.alarm_modules.filter((i) => i.acknowledged === 1)
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map(
        (o) => alarms.find((i) => i.id === o.id)?.name ?? ''
      ),
    [config.openedItems, alarms]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchAlarms);
  }, [config.openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<AlarmHistoryListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<AlarmHistoryListQuery | null>(
    () =>
      openedItem?.id
        ? {
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'date',
            dir: openedItem.params?.dir ?? 'DESC',
            message_type: openedItem.params?.message_type ?? [],
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: AlarmHistoryListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/alarm-module/${config.activeId}/history`;
        const resp = await API.get<AlarmHistoryListResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
        // collect all IDs
        if (openedItem && openedItem.selectAll) {
          openedItem.selectedIds = resp.data.items.map((el) =>
            getAlarmNodeHistoryId(el)
          );
        }
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_55aaa317");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);
  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<AlarmHistory>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      hideable: false,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(getAlarmNodeHistoryId(row))}
          onChange={() => toggleSelectItem(getAlarmNodeHistoryId(row))}
        />
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'type',
      headerName: 'Alarm',
      width: 40,
      renderHeader: () => {
        return <AlarmOutlined />;
      },
      renderCell: ({row}) => {
        if (row.alarm) {
          const tooltipContent = `
            <span>Alarm</span><br />
            <span>Reason: ${
              activationReason?.find((it) => it.key === row?.reason)?.label ??
              '_'
            }</span><br />
            <span>Reason Note: ${row.reason_note ?? '_'}</span>
          `;
          return (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{__html: tooltipContent}} />}
              arrow
            >
              <AlarmOutlined color="error" />
            </Tooltip>
          );
        } else if (row.warning) {
          const tooltipContent = `
            <span>Warning</span><br />
            <span>Reason: ${
              activationReason?.find((it) => it.key === row?.reason)?.label ??
              '_'
            }</span><br />
            <span>Reason Note: ${row.reason_note ?? '_'}</span>
          `;
          return (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{__html: tooltipContent}} />}
              arrow
            >
              <AlarmOutlined color="warning" />
            </Tooltip>
          );
        } else {
          return <></>;
        }
      },
    },
    {
      field: 'reason',
      headerName: 'Reason',
      sortable: true,
      width: 150,
      resizeable: true,
      sxCell: {whiteSpace: 'nowrap'},
      renderCell: ({row}) =>
        activationReason?.find((it) => it.key === row?.reason)?.label ?? '',
    },
    {
      field: 'reason_note',
      headerName: 'Note',
      width: 300,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => row.reason_note ?? '',
    },
    {
      field: 'user_id',
      headerName: 'User',
      width: 150,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => row?.user_name ?? '',
    },
    {
      field: 'message_type',
      headerName: 'Message Type',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return (
          MESSAGE_TYPE.find((it) => it.id === row.message_type)?.label ?? ''
        );
      },
    },
    {
      field: 'source_mac_address',
      headerName: 'Source Mac Address',
      width: 160,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => getHumanReadable(row.source_mac_address ?? ''),
    },
    {
      field: 'mac_address',
      headerName: 'Mac Address',
      width: 160,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => row.mac_address,
    },
    {
      field: 'date',
      headerName: 'Timestamp',
      width: 160,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => row.date,
    },
  ];

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(
    () =>
      openedItem?.selectAll
        ? rows.map((it) => getAlarmNodeHistoryId(it))
        : openedItem?.selectedIds ?? [],
    [rows, openedItem]
  );

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(getAlarmNodeHistoryId(i))),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const toggleSelectItem = (id: string) => {
    if (openedItem) {
      if (selectedItems?.includes(id)) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                $set: {
                  ...openedItem,
                  selectedIds: selectedItems.filter((i) => i !== id),
                  selectAll: false,
                },
              },
            },
          })
        );
      } else {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                $set: {
                  ...openedItem,
                  selectedIds: [...(selectedItems ?? []), id],
                  selectAll:
                    selectedItems.length + 1 === fetchedData?.items.length,
                },
              },
            },
          })
        );
      }
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: true,
              },
              selectedIds: {
                $set: rows?.map((i) => getAlarmNodeHistoryId(i)) ?? [],
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: false,
              },
              selectedIds: {
                $set: [],
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if (prevSelectedAll && !selectedAll) {
      selectAll();
    }
  }, [rows]);

  const openTab = (id: number) => {
    return handleOpenTab(id, config, onUpdate);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, onUpdate);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, onUpdate);
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              columnWidths: {$set: widths},
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = (fields: string[]) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              shownFields: {$set: fields},
            },
          },
        })
      );
    }
  };

  const handleColumnOrderAndShownFieldsChange = (
    order: string[],
    fields: string[]
  ) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              columnOrder: {$set: order},
              shownFields: {$set: fields},
            },
          },
        })
      );
    }
  };

  return (
    <>
      <DashboardPanelTitleSlot>Alarm History Reports</DashboardPanelTitleSlot>

      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        width="100%"
        overflow="hidden"
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          py={1.5}
          bgcolor={(theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
          }
        >
          {isOpenAwayFromConnectView && (
            <Box px={1.5}>
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <>
                    <Button variant="outlined" {...bindTrigger(popupState)}>
                      Select an Object
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      {alarms?.map((it) => (
                        <MenuItem
                          key={it.id}
                          onClick={() => {
                            it?.id && openTab(it.id);
                            popupState.close();
                          }}
                        >
                          {it.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </PopupState>
            </Box>
          )}
          {config.openedItems.length ? (
            <Box px={2}>
              <Tabs
                value={openedItem?.id}
                variant="scrollable"
                onChange={changeTab}
              >
                {config.openedItems.map((i, idx) => (
                  <Tab
                    key={i.id}
                    value={i.id}
                    label={
                      <TabLabel
                        name={tabNames?.[idx] ?? 'Undefined'}
                        onClose={() => closeTab(i.id)}
                      />
                    }
                  />
                ))}
              </Tabs>
            </Box>
          ) : (
            <Box minWidth={400} px={1.5}>
              <Alert severity="warning">No Alarms Opened</Alert>
            </Box>
          )}

          <Box display="flex" flexDirection="column" px={1.5}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Box display="flex" gap={1}>
                <Box minWidth={400}>
                  <DateRangeSelect
                    value={
                      openedItem
                        ? [
                            dayjs(openedItem.params?.date_start).toDate(),
                            dayjs(openedItem.params?.date_end).toDate(),
                          ]
                        : undefined
                    }
                    size="small"
                    disabled={!openedItem}
                    onChange={(v) => {
                      if (openedItem) {
                        onUpdate?.(
                          update(config, {
                            openedItems: {
                              [openedItemIndex]: {
                                $set: {
                                  ...openedItem,
                                  selectAll: true,
                                  params: {
                                    ...openedItem.params,
                                    date_start: v?.[0]
                                      ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                      : undefined,
                                    date_end: v?.[0]
                                      ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                      : undefined,
                                  },
                                },
                              },
                            },
                          })
                        );
                      }
                    }}
                  />
                </Box>
                <MessageTypeSelect
                  value={openedItem?.params?.message_type}
                  size="small"
                  type="message_type"
                  disabled={!openedItem}
                  options={MESSAGE_TYPE_OPTIONS}
                  onChange={(v) => {
                    if (openedItem) {
                      onUpdate?.(
                        update(config, {
                          openedItems: {
                            [openedItemIndex]: {
                              $set: {
                                ...openedItem,
                                selectAll: true,
                                params: {
                                  ...openedItem.params,
                                  message_type: v,
                                },
                              },
                            },
                          },
                        })
                      );
                    }
                  }}
                />
              </Box>

              <Box display="flex">
                <ButtonGroup disabled={!openedItem}>
                  <Button
                    size="small"
                    onClick={() => params && fetchData(params)}
                  >
                    <RefreshIcon />
                  </Button>

                  <AutoRefreshSelect
                    value={openedItem?.refreshInterval ?? null}
                    onChange={(v) => {
                      if (openedItem) {
                        onUpdate?.(
                          update(config, {
                            openedItems: {
                              [openedItemIndex]: {
                                $set: {
                                  ...openedItem,
                                  refreshInterval: v,
                                },
                              },
                            },
                          })
                        );
                      }
                    }}
                  />

                  <Button
                    size="small"
                    onClick={() => dataGridRef.current?.printTable()}
                  >
                    <PrintIcon />
                  </Button>
                </ButtonGroup>
              </Box>
            </Box>
          </Box>
        </Box>

        <Backdrop
          open={fetchedInProgress}
          sx={{position: 'absolute', zIndex: 1199}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {fetchedErrors.map((error, idx) => (
          <Alert
            key={idx}
            severity="error"
            onClose={() => params && fetchData(params)}
          >
            {error}
          </Alert>
        ))}

        <DataGrid
          ref={dataGridRef}
          rows={rows}
          columns={columns}
          loading={fetchedInProgress}
          shownFields={shownFields}
          defaultShownFields={DEFAULT_SHOWN_FIELDS}
          columnWidths={openedItem?.columnWidths}
          columnOrder={openedItem?.columnOrder}
          pagination
          paginationMode="server"
          size="small"
          // sx={{
          //   td: {
          //     whiteSpace: "nowrap",
          //   },
          // }}
          sortBy={
            params?.order
              ? {
                  field: params?.order,
                  dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                }
              : null
          }
          sortingMode="server"
          page={params?.page}
          pageSize={params?.limit}
          rowCount={fetchedData?.count}
          sxFooter={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
          }}
          onPageChange={(v) => {
            if (openedItem) {
              onUpdate?.(
                update(config, {
                  openedItems: {
                    [openedItemIndex]: {
                      $set: {
                        ...openedItem,
                        selectAll: true,
                        params: {
                          ...openedItem.params,
                          page: v,
                        },
                      },
                    },
                  },
                })
              );
            }
          }}
          onPageSizeChange={(v) => {
            if (openedItem) {
              onUpdate?.(
                update(config, {
                  openedItems: {
                    [openedItemIndex]: {
                      $set: {
                        ...openedItem,
                        selectAll: true,
                        params: {
                          ...openedItem.params,
                          page: 0,
                          limit: v,
                        },
                      },
                    },
                  },
                })
              );
            }
          }}
          onSort={(v) => {
            if (v && openedItem) {
              onUpdate?.(
                update(config, {
                  openedItems: {
                    [openedItemIndex]: {
                      $set: {
                        ...openedItem,
                        params: {
                          ...openedItem.params,
                          order: v.field,
                          dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                        },
                      },
                    },
                  },
                })
              );
            }
          }}
          onShownFieldsChange={handleShownFieldsChange}
          onColumnWidthsChange={handleColumnWidthChange}
          onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
        />
      </Box>
    </>
  );
};
